import { window } from "global";
import { useEffect } from "react";
import { useGlobal } from "reactn";
import { MediaQuery } from "@outplayed/responsive";
import { TierListHeader } from "@ugg/shared/components/TierList/TierListHeader";
import { TierListPageNav } from "@ugg/shared/components/TierList/TierListPageNav";
import { TierListFilterManager } from "@ugg/shared/components/TierList/TierListFilterManager";
import { TierListTablePlaceholder } from "@ugg/shared/components/TierList/common/TierListTablePlaceholder";
import { TierListTableError } from "@ugg/shared/components/TierList/common/TierListTableError";
import { useQueryString } from "@ugg/shared/hooks/use-query-string";
import { SPECIAL_TIER_LIST_PAGES } from "@ugg/shared/pages/tier-list-pages";
import { useValidateQueryParams } from "@ugg/shared/query-params/params-helpers";
import { validParams } from "@ugg/shared/query-params/valid-params";
import { ChampionLink } from "@ugg/shared/components/TierList/common/ChampionLink";
import { QueueTypeC, QueueTypeN } from "@ugg/shared/utils/queue-type-helpers";
import { useChampionProfileContext } from "@ugg/shared/components/Champions/ChampionProfileContext";
// Data layer (types, cleaners, JSONFetcher hooks) lives in the
// shared package so both this FE-specific tier-list container and
// any shared consumers (e.g. AramMayhemSEO) can use it. This file
// consumes the hooks + types; UI-only concerns (styling constants,
// components, page containers) stay local.
import {
  TIER_ORDER,
  TierNameValue,
  AugmentRarity,
  AugmentManifest,
  useAramMayhemChampionTierList,
  useAramMayhemAugmentTierList,
  useAramMayhemAugmentManifest,
  useAramMayhemPerChampionAugmentTierList,
} from "@ugg/shared/hooks/aram-mayhem";

type BannerProp = {
  bannerSlot?: JSX.Element | null;
  patchNotesCTA?: React.ReactNode;
};

// ─────────────────────────────────────────────────────────────────────
// Styling helpers
// ─────────────────────────────────────────────────────────────────────

// Per-tier solid background class. Used in three places per card:
// the 4px left border (via the matching `border-[#X]` variant in
// TIER_BANNER_CLASSES), the 1px horizontal divider between banner
// and body, and the small vertical accent bar that sits just left
// of the tier letter inside the banner. Kept as a const map (rather
// than computed) so Tailwind's content scanner picks up the literal
// class strings at build time.
const TIER_LABEL_BG: Record<TierNameValue, string> = {
  "S+": "bg-[#FF9B00]",
  S: "bg-[#3273FA]",
  A: "bg-[#7EA4F4]",
  B: "bg-[#CDDCFE]",
  C: "bg-[#FCB1B2]",
  D: "bg-[#FF4E50]",
};

// Banner styling per tier — combines the border color (applied as
// 4px left border on banner and body, also as the matching divider
// 1px line color via TIER_LABEL_BG) with the gradient start color.
// Full class strings (not template-string interpolations) so
// Tailwind's JIT content scanner picks them up at build time.
//
// The gradient is `bg-gradient-to-r from-[#X]/<opacity> to-transparent`,
// applied to a banner that already has `bg-[#191937]` underneath —
// the tier color fades horizontally from 30% opacity at the left
// to fully transparent at the right, revealing the dark banner
// base. Opacity is 30% for every tier except B, where /20 is used
// because the much lighter #CDDCFE would otherwise overpower the
// gradient.
const TIER_BANNER_CLASSES: Record<TierNameValue, string> = {
  "S+": "border-[#FF9B00] from-[#FF9B00]/30",
  S: "border-[#3273FA] from-[#3273FA]/30",
  A: "border-[#7EA4F4] from-[#7EA4F4]/30",
  B: "border-[#CDDCFE] from-[#CDDCFE]/20",
  C: "border-[#FCB1B2] from-[#FCB1B2]/30",
  D: "border-[#FF4E50] from-[#FF4E50]/30",
};

// Body background color per tier — alternates between the lighter
// (#191937) and darker (#11112A) navy shades to zebra-stripe the
// stacked tier cards. Matches the Figma spec exactly: S+/A/C use
// the lighter shade, S/B/D use the darker. The lighter color also
// matches the banner background, so for the "lighter" tiers
// (S+/A/C) the banner and body share a single background and the
// 1px tier-color divider is the only visual separator between
// them; for the "darker" tiers (S/B/D) the banner sits visibly
// brighter than the body.
const TIER_BODY_BG: Record<TierNameValue, string> = {
  "S+": "bg-[#191937]",
  S: "bg-[#11112A]",
  A: "bg-[#191937]",
  B: "bg-[#11112A]",
  C: "bg-[#191937]",
  D: "bg-[#11112A]",
};

// Iteration order for the by-rarity augment table — Prismatic → Gold
// → Silver, matching the canonical RARITY_DISPLAY_ORDER on the Python
// side (best-to-worst). Explicit constant (rather than Object.keys()
// over the fetched data) so the React order stays stable even if
// upstream JSON key ordering ever changes.
const RARITY_ORDER = ["kPrismatic", "kGold", "kSilver"] as const satisfies readonly AugmentRarity[];

// Human-readable rarity labels for section headers. Decoupled from
// the JSON's `k*` enum keys so localization / display tweaks live
// here instead of in every render site.
const RARITY_DISPLAY: Record<AugmentRarity, string> = {
  kPrismatic: "Prismatic",
  kGold: "Gold",
  kSilver: "Silver",
};

// ─────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────

interface AramMayhemChampionTierListTableProps {
  tierlist: Record<TierNameValue, number[]>;
}

export function AramMayhemChampionTierListTable(props: AramMayhemChampionTierListTableProps) {
  const { tierlist } = props || {};
  const convertedQueueType = QueueTypeC.convertToString(QueueTypeN.ARAM_MAYHEM) as string;

  return (
    // Vertical stack of tier cards with 24px gaps between them
    // (matches the Figma spec — bumped from gap-3 in the previous
    // iteration to gap-6 to match the reference more closely).
    <div className="flex flex-col gap-6">
      {TIER_ORDER.map((tier) => {
        const champions = tierlist[tier];
        // Skip empty tiers — no "Empty S+" rows. Comment this guard
        // out if you want all 6 tier rows visible regardless.
        if (!champions || champions.length === 0) return null;
        return (
          <section
            key={tier}
            aria-label={`Tier ${tier}`}
            // Card shell: 3px rounded corners. overflow-hidden so
            // the banner's top corners and the body's bottom
            // corners clip cleanly to the rounded outer shape.
            // Note: the 4px left border is applied to the banner
            // and body individually (matching Figma's structure)
            // rather than to this section — that way the 1px
            // horizontal divider can span the full card width
            // including the left-border column, giving a
            // continuous colored "L+T" frame on the left and top
            // of the body.
            className="overflow-hidden rounded-[3px]"
          >
            {/* Banner — fixed 56px height (h-14), 4px left border
                in tier color, dark base background (#191937 — a
                shade lighter than the body to subtly differentiate
                the two regions), and a horizontal gradient that
                fades the tier color from 30% opacity at the left
                to transparent at the right. The h-14 + flex
                items-center keeps the label vertically centered. */}
            <div
              className={`flex h-14 items-center gap-3 border-l-4 bg-[#191937] bg-gradient-to-r to-transparent px-[18px] ${TIER_BANNER_CLASSES[tier]}`}
            >
              <span className={`block h-5 w-0.5 rounded-sm ${TIER_LABEL_BG[tier]}`} aria-hidden="true" />
              <h2 className="text-sm font-bold text-white">{tier}</h2>
            </div>
            {/* 1px horizontal divider in the tier color, spanning
                the full card width (no left border on this div, so
                the colored line extends seamlessly over the area
                where the banner / body left borders sit). */}
            <div className={`h-px ${TIER_LABEL_BG[tier]}`} aria-hidden="true" />
            {/* Body — per-tier alternating background (lighter
                #191937 for S+/A/C, darker #11112A for S/B/D —
                gives the stacked tier cards a subtle zebra-stripe
                effect from the Figma spec). 4px left border
                continues the column from the banner. Champion
                grid scales 1 / 2 / 3 / 4 / 5 columns across
                breakpoints.

                The `className` override on <ChampionLink> uses a
                Tailwind arbitrary descendant variant to reach the
                inner <strong class="champion-name"> and force a
                14px font size, matching Figma's section-item
                spec. The shared CSS class otherwise locks the
                size at 13px; the `!` prefix makes the utility
                !important so it wins regardless of stylesheet
                load order.

                `!gap-[18px]` applies directly to the ChampionLink
                <a>, which the DOM confirms is the actual flex
                container (`flex items-center justify-center`).
                Its two direct children are the icon-wrapper div
                and the .champion-name strong element — sibling
                flex items, so a `gap-` on the parent puts space
                between them. Restores the ~18px Figma-spec
                breathing room that was previously provided by
                the icon-wrapper's `px-[10px]` right padding
                (which our `!px-0` above collapses for accent-bar
                alignment).

                Note: the strong element also has an `ml-[2px]`
                internal to ChampionLink, so the effective visual
                gap is ~20px rather than exactly 18px. If pixel-
                precise matching to Figma matters, drop the value
                to `!gap-[16px]` to compensate, or add
                `[&_.champion-name]:!ml-0` to zero out that inner
                margin. */}
            <div
              className={`grid grid-cols-1 gap-x-[18px] gap-y-[18px] border-l-4 p-[18px] sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 ${TIER_BODY_BG[tier]} ${TIER_BANNER_CLASSES[tier]}`}
            >
              {champions.map((championId) => (
                <ChampionLink
                  key={championId}
                  championId={championId}
                  queueType={convertedQueueType}
                  dim={36}
                  className="[&_.champion-name]:!text-sm [&>div]:!px-0 !gap-[18px]"
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Augment tier rows — private inner helper shared by both augment
// tables. Renders one set of tier rows from a {tier: [augment_id]}
// map. Lifted out so AramMayhemAugmentTierListTable (one set of rows,
// flat) and AramMayhemAugmentByRarityTierListTable (one set per
// rarity, three sections) don't duplicate the same render logic.
//
// Visual layout mirrors the champion table — same tier-row card,
// same colored left bar, same responsive grid — so the augment and
// champion sections feel visually consistent when stacked on the
// same page. The per-cell content is a placeholder until the augment
// asset pipeline lands: a dashed-border square stands in for the
// future icon, with the augment ID rendered as text beside it.
// Swap the inner card contents (icon img, localized name, hover
// behavior) without touching this surrounding structure.
// ─────────────────────────────────────────────────────────────────────

interface AugmentTierRowsProps {
  tierlist: Record<TierNameValue, number[]>;
  manifest: AugmentManifest;
  patch: string;
}

function AugmentTierRows(props: AugmentTierRowsProps) {
  const { tierlist, manifest, patch } = props || {};

  return (
    <div className="flex flex-col gap-6">
      {TIER_ORDER.map((tier) => {
        const augmentIds = tierlist[tier];
        // Skip empty tiers — matches the champion table's behavior.
        if (!augmentIds || augmentIds.length === 0) return null;
        return (
          <section key={tier} aria-label={`Tier ${tier}`} className="overflow-hidden rounded-[3px]">
            {/* Banner — same structure as the champion table:
                h-14, 4px left border, dark base bg with horizontal
                tier-color gradient, small vertical accent bar to
                the left of a 14px bold white tier letter. */}
            <div
              className={`flex h-14 items-center gap-3 border-l-4 bg-[#191937] bg-gradient-to-r to-transparent px-[18px] ${TIER_BANNER_CLASSES[tier]}`}
            >
              <span className={`block h-5 w-0.5 rounded-sm ${TIER_LABEL_BG[tier]}`} aria-hidden="true" />
              <h2 className="text-sm font-bold text-white">{tier}</h2>
            </div>
            <div className={`h-px ${TIER_LABEL_BG[tier]}`} aria-hidden="true" />
            {/* Augment grid — same column breakpoints + left border
                + dark background as the champion body. */}
            <div
              className={`grid grid-cols-1 gap-x-[18px] gap-y-[18px] border-l-4 p-[18px] sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 ${TIER_BODY_BG[tier]} ${TIER_BANNER_CLASSES[tier]}`}
            >
              {augmentIds.map((augmentId) => {
                // Manifest is keyed by stringified id (JSON object keys
                // must be strings). Look up here once per cell.
                const name = manifest[String(augmentId)];
                if (!name) {
                  // Fallback for ids not present in the manifest —
                  // shouldn't happen with a fresh manifest fetched on
                  // the same patch, but possible if the tier-list and
                  // manifest get out of sync, or if the manifest is
                  // still loading and somehow rendered early. The
                  // dashed-square + #id form is the same placeholder
                  // we had before the manifest existed, so it's
                  // visually obvious when this branch fires.
                  // Sized to match the real entry (h-9 / 36px) so
                  // layout doesn't shift when entries become available.
                  return (
                    <div key={augmentId} className="flex items-center gap-2">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[3px] border border-dashed border-[#1c2030] bg-[#1a1d2e] font-mono text-[10px] text-[#64748b]">
                        ?
                      </div>
                      <span className="font-mono text-sm text-[#cddcea]">#{augmentId}</span>
                    </div>
                  );
                }
                return (
                  // Real cell — 36×36 icon + name in 14px Barlow
                  // semibold white, matching the Figma item spec.
                  // Icon uses 3px rounded corners (rounded-[3px])
                  // to match Figma's `border-radius: 3px` on the
                  // image mask. lazy loading keeps offscreen tier
                  // rows from hitting the network until the user
                  // scrolls down.
                  <div key={augmentId} className="flex items-center gap-2">
                    <img
                      src={`https://static.bigbrain.gg/cdragon-custom/${patch}/augments/${augmentId}.webp`}
                      alt={name}
                      loading="lazy"
                      className="h-9 w-9 shrink-0 rounded-[3px]"
                    />
                    <span className="font-[Barlow] text-sm font-semibold text-white">{name}</span>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Aggregated augment table — one global tier list across all
// rarities. Thin wrapper around AugmentTierRows; the wrapping exists
// for API symmetry with the champion table (both are exported as
// "...TierListTable") and for a clear extension point if the
// aggregated view eventually grows page-level chrome (filters,
// sort toggles, etc.).
// ─────────────────────────────────────────────────────────────────────

interface AramMayhemAugmentTierListTableProps {
  tierlist: Record<TierNameValue, number[]>;
  manifest: AugmentManifest;
  patch: string;
}

export function AramMayhemAugmentTierListTable(props: AramMayhemAugmentTierListTableProps) {
  const { tierlist, manifest, patch } = props || {};
  return <AugmentTierRows tierlist={tierlist} manifest={manifest} patch={patch} />;
}

// ─────────────────────────────────────────────────────────────────────
// By-rarity augment table — three sections (Prismatic / Gold /
// Silver), each containing an independent tier grouping. Iterates
// in the canonical best-to-worst order from RARITY_ORDER. A section
// is hidden entirely if its `tiers` payload is missing (defensive
// — the JSON always carries all three rarities, but a partial
// fetch or future schema variation shouldn't crash the page).
// ─────────────────────────────────────────────────────────────────────

interface AramMayhemAugmentByRarityTierListTableProps {
  rarities: Record<AugmentRarity, { tiers: Record<TierNameValue, number[]> }>;
  manifest: AugmentManifest;
  patch: string;
}

export function AramMayhemAugmentByRarityTierListTable(props: AramMayhemAugmentByRarityTierListTableProps) {
  const { rarities, manifest, patch } = props || {};

  return (
    // Larger gap between rarity sections than between tier rows —
    // helps visually separate the three independent groupings.
    <div className="flex flex-col gap-6">
      {RARITY_ORDER.map((rarity) => {
        const rarityTiers = rarities?.[rarity]?.tiers;
        if (!rarityTiers) return null;
        return (
          <section key={rarity} className="flex flex-col gap-2">
            {/* Rarity header — plain heading for now. Easy place
                to layer in color coding (e.g. orange for Prismatic,
                gold for Gold, gray for Silver) once we settle on
                the visual treatment. */}
            <h3 className="text-lg font-bold text-[#e2e8f0]">{RARITY_DISPLAY[rarity]}</h3>
            <AugmentTierRows tierlist={rarityTiers} manifest={manifest} patch={patch} />
          </section>
        );
      })}
    </div>
  );
}

export const AramMayhemChampionTierListContainer = ({ bannerSlot, patchNotesCTA }: BannerProp) => {
  const queryParams = useQueryString();

  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(SPECIAL_TIER_LIST_PAGES.ARAM_MAYHEM_TIER_LIST, queryParams, true);
  const { patch } = validatedQueryParams;

  // Splice the validated patch back into the params dict so hooks
  // that read `patch` directly off `params` (manifest, etc.) see it
  // even when the URL omits the explicit `?patch=...` query —
  // validateQueryParams falls back to the latest patch when the
  // query key is missing, and we want every downstream fetch to
  // share that fallback rather than each hook having to know about
  // it independently. We DO still pass the same dict to the
  // tier-list hooks below (which run their own validateQueryParams
  // pass internally) — adding the resolved `patch` key here is a
  // no-op for them and a fix for the direct-params hooks.
  const params = { ...queryParams, patch };

  const { data, loading, error } = useAramMayhemChampionTierList({
    params,
  });
  const { tierlist, lastUpdated } = data || {};

  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, []);

  let table = null;
  if (!window) {
    table = null;
  } else if (loading) {
    table = <TierListTablePlaceholder />;
  } else if (error) {
    table = <TierListTableError />;
  } else {
    table = <AramMayhemChampionTierListTable tierlist={tierlist || ({} as Record<TierNameValue, number[]>)} />;
  }

  return (
    <div>
      <TierListHeader
        className="mb-[24px]"
        totalMatches={0}
        lastUpdated={lastUpdated}
        loading={loading}
        includeMeta
        patchNotesCTA={patchNotesCTA}
      />
      <MediaQuery min="TABLET" max="DESKTOP_LARGE">
        <div>
          <TierListPageNav />
        </div>
      </MediaQuery>
      {bannerSlot ?? null}
      <TierListFilterManager className="my-[24px]" />
      <>{table}</>
    </div>
  );
};

export const AramMayhemAugmentTierListContainer = ({ bannerSlot, patchNotesCTA }: BannerProp) => {
  const queryParams = useQueryString();

  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(SPECIAL_TIER_LIST_PAGES.ARAM_MAYHEM_AUGMENT_TIER_LIST, queryParams, true);
  const { patch } = validatedQueryParams;

  // Splice the validated patch back into the params dict — same
  // rationale as the champion container above. Specifically
  // important here because this container drives the manifest
  // fetch, and the manifest hook (post-refactor) reads `patch`
  // directly off params without running its own
  // validateQueryParams. Without this enrichment, URLs that omit
  // `?patch=...` would skip the manifest fetch entirely (via the
  // hook's effectiveSkip guard) and the augment tables would
  // render with placeholder content.
  const params = { ...queryParams, patch };

  // Augment data — aggregated. Independent fetch from the champion
  // one so a slow augment load doesn't block the champion table from
  // rendering (and vice versa). Variable names are prefixed to avoid
  // shadowing the champion destructures above.
  const { data: augmentData, loading: augmentLoading, error: augmentError } = useAramMayhemAugmentTierList({ params });
  const { tierlist: augmentTierlist, lastUpdated } = augmentData || {};

  // Augment manifest — flat id → display-name dict (see aram-mayhem.ts
  // for the shape). Both augment tables look up against it to render
  // real display names alongside the id-templated icon URLs. Fetched
  // once and prop-drilled to the tables so the JSON fetcher's cache
  // isn't strictly necessary for correctness.
  const { data: manifestData, loading: manifestLoading, error: manifestError } = useAramMayhemAugmentManifest({ params });
  const manifest = manifestData || ({} as AugmentManifest);

  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, []);

  // Augment table — same loading/error/render pattern as the champion
  // one but driven by the separate augment fetch. SSR guard mirrors
  // the existing pattern (skip render until `window` is defined).
  // The manifest fetch is folded into the augment table's loading +
  // error gating because the table can't render meaningful content
  // without the manifest — defer the table until BOTH are ready,
  // rather than showing one render with placeholder fallback and
  // then a second render once the manifest arrives.
  let augmentTable = null;
  if (!window) {
    augmentTable = null;
  } else if (augmentLoading || manifestLoading) {
    augmentTable = <TierListTablePlaceholder />;
  } else if (augmentError || manifestError) {
    augmentTable = <TierListTableError />;
  } else {
    augmentTable = (
      <AramMayhemAugmentTierListTable
        tierlist={augmentTierlist || ({} as Record<TierNameValue, number[]>)}
        manifest={manifest}
        patch={patch}
      />
    );
  }

  return (
    <div>
      <TierListHeader
        className="mb-[24px]"
        totalMatches={0}
        lastUpdated={lastUpdated}
        loading={augmentLoading || manifestLoading}
        includeMeta
        patchNotesCTA={patchNotesCTA}
      />
      <MediaQuery min="TABLET" max="DESKTOP_LARGE">
        <div>
          <TierListPageNav />
        </div>
      </MediaQuery>
      {bannerSlot ?? null}
      <TierListFilterManager className="my-[24px]" />
      <>{augmentTable}</>
    </div>
  );
};

//--------------------------------------------------------------------------------

// Per-champion augment tier list, scoped to ONE champion's
// observations of augments. Renders inside the champion-build page
// (not the tier-list page), so the data flow differs from the
// route-bound containers above in two ways: the caller passes
// `championKey` (numeric riot champion id — 62 for MonkeyKing, 145
// for Kaisa, ...) and `params` explicitly rather than letting the
// container scrape them from useQueryString / validateQueryParams;
// and the page-chrome (TierListHeader, MediaQuery + TierListPageNav,
// TierListFilterManager, banner slot, patch-notes CTA) is omitted
// because the build page provides its own surrounding layout.
//
// The same `AramMayhemAugmentTierListTable` used by the global
// augment container above is reused here — the JSON shape is
// identical (id → tier mapping), so there's no rendering
// difference, just a different fetch source.
//
// Manifest fetching: we reuse `useAramMayhemAugmentManifest` from
// above, which (post-refactor) also reads `patch` directly off
// caller-supplied params rather than running its own
// validateQueryParams. So the same `params` dict that drives the
// per-champion tier-list fetch above drives the manifest fetch
// here — no second resolution path, no risk of the two hooks
// disagreeing on which patch they're targeting. Both hooks'
// effectiveSkip guards short-circuit cleanly if `patch` ever
// goes missing.
interface AramMayhemPerChampionAugmentTierListContainerProps {
  championKey: number;
  params: Record<string, any>;
}

export const AramMayhemPerChampionAugmentTierListContainer = ({
  championKey,
  params,
}: AramMayhemPerChampionAugmentTierListContainerProps) => {
  // Per-champion tier list — bypasses validateQueryParams, takes
  // patch straight off the caller-supplied params dict (see the
  // hook's own comment for the rationale).
  const {
    data: augmentData,
    loading: augmentLoading,
    error: augmentError,
  } = useAramMayhemPerChampionAugmentTierList({ championKey, params });
  const { tierlist: augmentTierlist } = augmentData || {};

  // Augment manifest — shared lookup table for icons + display
  // names. The augment table can render placeholders without the
  // manifest, but the result is unreadable (just "#1234" labels),
  // so we gate the table on BOTH fetches being ready, same as the
  // global augment container.
  const { data: manifestData, loading: manifestLoading, error: manifestError } = useAramMayhemAugmentManifest({ params });
  const manifest = manifestData || ({} as AugmentManifest);

  const { patch } = params;

  // Loading / error / render gating. typeof window check guards
  // SSR — JSONFetcher works in both environments but the table's
  // <img> tags fire lazy loads that only make sense client-side.
  let table = null;
  if (typeof window === "undefined") {
    table = null;
  } else if (augmentLoading || manifestLoading) {
    table = <TierListTablePlaceholder />;
  } else if (augmentError || manifestError) {
    table = <TierListTableError />;
  } else {
    table = (
      <AramMayhemAugmentTierListTable
        tierlist={augmentTierlist || ({} as Record<TierNameValue, number[]>)}
        manifest={manifest}
        patch={patch}
      />
    );
  }

  return <div>{table}</div>;
};

//--------------------------------------------------------------------------------

const AramMayhemBuild = () => {
  const { championId, page } = useChampionProfileContext();

  // championId is the numeric champion KEY (62 for MonkeyKing, 145
  // for Kaisa, ...) — the same identifier data_tierlists-html.py
  // uses as the filename stem for the per-champion augment JSONs
  // (after resolving SKIN → key via the mapping file). We pass it
  // through as-is; no name conversion needed anymore.

  const queryParams = useQueryString();
  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(page, queryParams, true);

  return <AramMayhemPerChampionAugmentTierListContainer championKey={championId} params={validatedQueryParams} />;
};

export default AramMayhemBuild;

//--------------------------------------------------------------------------------
